import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Wrench, AlertTriangle, ShieldCheck, Settings2, Send, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
    { id: "warranty", label: "Garantia", icon: ShieldCheck },
    { id: "maintenance", label: "Manutenção", icon: Settings2 },
    { id: "issue", label: "Avaria", icon: AlertTriangle },
    { id: "other", label: "Outro", icon: Wrench },
];

const STATUS_LABEL = {
    open: "Aberto", in_progress: "Em curso", scheduled: "Agendado", resolved: "Resolvido", closed: "Fechado",
};
const STATUS_CLASS = {
    open: "bg-blue-100 text-blue-700 border-blue-200",
    in_progress: "bg-amber-100 text-amber-700 border-amber-200",
    scheduled: "bg-violet-100 text-violet-700 border-violet-200",
    resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function ContaAssistencia() {
    const location = useLocation();
    const preEquipmentId = location.state?.equipmentId || "";

    const [equipments, setEquipments] = useState([]);
    const [items, setItems] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        type: "issue",
        title: "",
        description: "",
        equipment_id: preEquipmentId,
        contact_phone: "",
    });

    async function loadAll() {
        try {
            const [eq, sr] = await Promise.all([
                api.get("/account/equipments"),
                api.get("/account/service-requests"),
            ]);
            setEquipments(eq.data);
            setItems(sr.data);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar dados.");
            setItems([]);
        }
    }

    useEffect(() => { loadAll(); }, []);

    function onChange(e) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/account/service-requests", {
                type: form.type,
                title: form.title.trim(),
                description: form.description.trim(),
                equipment_id: form.equipment_id || null,
                contact_phone: form.contact_phone.trim() || null,
            });
            toast.success("Pedido submetido. A nossa equipa entrará em contacto.");
            setForm({ type: "issue", title: "", description: "", equipment_id: "", contact_phone: "" });
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao submeter pedido.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="conta-assistencia">
            <h2 className="font-heading text-2xl font-extrabold text-primary">Assistência técnica</h2>
            <p className="text-sm text-muted-foreground">Abra um pedido de garantia, manutenção ou avaria. Resposta tipicamente em 24h úteis.</p>

            <div className="mt-8 grid gap-8 lg:grid-cols-5">
                {/* Form */}
                <form onSubmit={onSubmit} className="lg:col-span-2 rounded-2xl border border-border bg-white p-6 lg:p-6 sm:p-8 space-y-5" data-testid="assist-form">
                    <h3 className="font-heading text-lg font-extrabold text-primary">Novo pedido</h3>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Tipo</label>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            {TYPES.map(({ id, label, icon: Icon }) => (
                                <button key={id} type="button"
                                    data-testid={`assist-type-${id}`}
                                    onClick={() => setForm((f) => ({ ...f, type: id }))}
                                    className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-xs font-bold transition ${form.type === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-muted text-primary hover:border-primary/30"}`}>
                                    <Icon className="h-3.5 w-3.5" /> {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {equipments.length > 0 && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Equipamento (opcional)</label>
                            <select name="equipment_id" value={form.equipment_id} onChange={onChange}
                                data-testid="assist-equipment"
                                className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white">
                                <option value="">— Nenhum específico —</option>
                                {equipments.map((eq) => (
                                    <option key={eq.id} value={eq.id}>
                                        {eq.nickname || eq.serial_number} · {eq.model}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Assunto</label>
                        <input type="text" name="title" required value={form.title} onChange={onChange}
                            data-testid="assist-title"
                            className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                            placeholder="Ex: Bateria não carrega" />
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Descrição</label>
                        <textarea name="description" required rows={5} value={form.description} onChange={onChange}
                            data-testid="assist-description"
                            className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                            placeholder="Descreva o problema com o máximo de detalhe possível..." />
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Telefone para contacto</label>
                        <input type="text" name="contact_phone" value={form.contact_phone} onChange={onChange}
                            data-testid="assist-phone"
                            className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                            placeholder="(opcional)" />
                    </div>

                    <button type="submit" disabled={submitting}
                        data-testid="assist-submit"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:-translate-y-0.5 disabled:opacity-60">
                        {submitting ? "A enviar..." : (<>Submeter pedido <Send className="h-4 w-4" /></>)}
                    </button>
                </form>

                {/* List */}
                <div className="lg:col-span-3">
                    <h3 className="font-heading text-lg font-extrabold text-primary">Histórico de pedidos</h3>

                    {items === null && <div className="mt-4 text-sm text-muted-foreground">A carregar...</div>}

                    {items && items.length === 0 && (
                        <div className="mt-4 rounded-2xl border-2 border-dashed border-border bg-white p-6 sm:p-8 text-center" data-testid="assist-empty">
                            <Wrench className="h-6 w-6 text-primary/40 mx-auto" />
                            <p className="mt-3 text-sm text-muted-foreground">Sem pedidos abertos.</p>
                        </div>
                    )}

                    {items && items.length > 0 && (
                        <ul className="mt-4 space-y-3" data-testid="assist-list">
                            {items.map((it) => (
                                <li key={it.id} className="rounded-xl border border-border bg-white p-5" data-testid={`assist-item-${it.id}`}>
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className="font-heading text-base font-extrabold text-primary">{it.title}</h4>
                                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[it.status] || STATUS_CLASS.open}`}>
                                                    {STATUS_LABEL[it.status] || it.status}
                                                </span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                                                <Clock className="h-3 w-3" /> {fmt(it.created_at)} · <span className="uppercase font-bold">{it.type}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-3 text-sm text-primary/80 whitespace-pre-line">{it.description}</p>
                                    {it.admin_response && (
                                        <div className="mt-3 rounded-md bg-secondary/30 border border-secondary/50 p-3">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Resposta NOVIFOOD</div>
                                            <p className="mt-1 text-sm text-primary whitespace-pre-line">{it.admin_response}</p>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function fmt(s) {
    if (!s) return "—";
    try {
        const d = typeof s === "string" ? new Date(s) : s;
        return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return String(s); }
}
